import { Group } from '@antv/g';
import { groupBy } from '@antv/util';
import { AnimationType, COMBO_KEY, GraphEvent } from '../constants';
import { ELEMENT_TYPES } from '../constants/element';
import { getExtension } from '../registry';
import { cacheStyle, getCachedStyle, hasCachedStyle, setCacheStyle } from '../utils/cache';
import { reduceDataChanges } from '../utils/change';
import { isCollapsed } from '../utils/collapsibility';
import { markToBeDestroyed, updateStyle } from '../utils/element';
import { AnimateEvent, ElementLifeCycleEvent, GraphLifeCycleEvent, emit } from '../utils/event';
import { idOf } from '../utils/id';
import { assignColorByPalette, parsePalette } from '../utils/palette';
import { positionOf } from '../utils/position';
import { print } from '../utils/print';
import { computeElementCallbackStyle } from '../utils/style';
import { themeOf } from '../utils/theme';
export class ElementController {
    context;
    container;
    elementMap = {};
    shapeTypeMap = {};
    constructor(context) {
        this.context = context;
    }
    init() {
        if (!this.container) {
            const { canvas } = this.context;
            this.container = canvas.appendChild(new Group());
        }
    }
    emit(event, context) {
        if (context.silence)
            return;
        emit(this.context.graph, event);
    }
    forEachElementData(callback) {
        ELEMENT_TYPES.forEach((elementType) => {
            const elementData = this.context.model.getElementsDataByType(elementType);
            callback(elementType, elementData);
        });
    }
    getElementType(elementType, datum) {
        const { options, graph } = this.context;
        const userDefinedType = options[elementType]?.type || datum.type;
        if (!userDefinedType) {
            if (elementType === 'edge')
                return 'line';
            // node / combo
            else
                return 'circle';
        }
        if (typeof userDefinedType === 'string')
            return userDefinedType;
        // @ts-expect-error skip type check
        return userDefinedType.call(graph, datum);
    }
    getTheme(elementType) {
        return themeOf(this.context.options)[elementType] || {};
    }
    getThemeStyle(elementType) {
        return this.getTheme(elementType).style || {};
    }
    getThemeStateStyle(elementType, states) {
        const { state = {} } = this.getTheme(elementType);
        return Object.assign({}, ...states.map((name) => state[name] || {}));
    }
    paletteStyle = {};
    computePaletteStyle() {
        const { options } = this.context;
        this.paletteStyle = {};
        this.forEachElementData((elementType, elementData) => {
            const palette = Object.assign({}, parsePalette(this.getTheme(elementType)?.palette), parsePalette(options[elementType]?.palette));
            if (palette?.field) {
                Object.assign(this.paletteStyle, assignColorByPalette(elementData, palette));
            }
        });
    }
    getPaletteStyle(elementType, id) {
        const color = this.paletteStyle[id];
        if (!color)
            return {};
        if (elementType === 'edge')
            return { stroke: color };
        return { fill: color };
    }
    defaultStyle = {};
    /**
     * <zh/> 计算单个元素的默认样式
     *
     * <en/> compute default style of single element
     */
    computeElementDefaultStyle(elementType, context) {
        const { options } = this.context;
        const defaultStyle = options[elementType]?.style || {};
        this.defaultStyle[idOf(context.datum)] = computeElementCallbackStyle(defaultStyle, context);
    }
    computeElementsDefaultStyle(ids) {
        const { graph } = this.context;
        this.forEachElementData((elementType, elementData) => {
            const length = elementData.length;
            for (let i = 0; i < length; i++) {
                const datum = elementData[i];
                if (ids === undefined || ids.includes(idOf(datum))) {
                    this.computeElementDefaultStyle(elementType, { datum, graph });
                }
            }
        });
    }
    getDefaultStyle(id) {
        return this.defaultStyle[id] || {};
    }
    getElementState(id) {
        try {
            const { model } = this.context;
            return model.getElementState(id);
        }
        catch {
            return [];
        }
    }
    stateStyle = {};
    /**
     * <zh/> 获取单个元素的单个状态的样式
     *
     * <en/> get single state style of single element
     */
    getElementStateStyle(elementType, state, context) {
        const { options } = this.context;
        const stateStyle = options[elementType]?.state?.[state] || {};
        return computeElementCallbackStyle(stateStyle, context);
    }
    /**
     * <zh/> 计算单个元素的合并状态样式
     *
     * <en/> compute merged state style of single element
     */
    computeElementStatesStyle(elementType, states, context) {
        this.stateStyle[idOf(context.datum)] = Object.assign({}, ...states.map((state) => this.getElementStateStyle(elementType, state, context)));
    }
    /**
     * <zh/> 计算全部元素的状态样式
     *
     * <en/> compute state style of all elements
     * @param ids - <zh/> 计算指定元素的状态样式 | <en/> compute state style of specified elements
     */
    computeElementsStatesStyle(ids) {
        const { graph } = this.context;
        this.forEachElementData((elementType, elementData) => {
            const length = elementData.length;
            for (let i = 0; i < length; i++) {
                const datum = elementData[i];
                if (ids === undefined || ids.includes(idOf(datum))) {
                    const states = this.getElementState(idOf(datum));
                    this.computeElementStatesStyle(elementType, states, { datum, graph });
                }
            }
        });
    }
    getStateStyle(id) {
        return this.stateStyle[id] || {};
    }
    computeStyle(stage, ids) {
        const skip = ['translate', 'zIndex'];
        if (stage && skip.includes(stage))
            return;
        this.computePaletteStyle();
        this.computeElementsDefaultStyle(ids);
        this.computeElementsStatesStyle(ids);
    }
    getElement(id) {
        return this.elementMap[id];
    }
    getElementZIndex(id) {
        const element = this.getElement(id);
        if (!element)
            return 0;
        return element.style.zIndex ?? 0;
    }
    getNodes() {
        return this.context.model.getNodeData().map(({ id }) => this.elementMap[id]);
    }
    getEdges() {
        return this.context.model.getEdgeData().map((edge) => this.elementMap[idOf(edge)]);
    }
    getCombos() {
        return this.context.model.getComboData().map(({ id }) => this.elementMap[id]);
    }
    getElementComputedStyle(elementType, datum) {
        const id = idOf(datum);
        // 优先级(从低到高) Priority (from low to high):
        const themeStyle = this.getThemeStyle(elementType);
        const paletteStyle = this.getPaletteStyle(elementType, id);
        const dataStyle = datum.style || {};
        const defaultStyle = this.getDefaultStyle(id);
        const themeStateStyle = this.getThemeStateStyle(elementType, this.getElementState(id));
        const stateStyle = this.getStateStyle(id);
        const style = Object.assign({}, themeStyle, paletteStyle, dataStyle, defaultStyle, themeStateStyle, stateStyle);
        if (elementType === 'combo') {
            const childrenData = this.context.model.getChildrenData(id);
            const isCollapsed = !!style.collapsed;
            const childrenNode = isCollapsed ? [] : childrenData.map(idOf).filter((id) => this.getElement(id));
            Object.assign(style, { childrenNode, childrenData });
        }
        return style;
    }
    /**
     * <zh/> 开始绘制流程
     *
     * <en/> start render process
     */
    draw(context = { animation: true }) {
        this.init();
        const data = this.computeChangesAndDrawData(context);
        if (!data)
            return null;
        const { dataChanges, drawData } = data;
        this.markDestroyElement(drawData);
        // 计算样式 / Calculate style
        this.computeStyle(context.stage);
        // 创建渲染任务 / Create render task
        const { add, update, remove } = drawData;
        this.destroyElements(remove, context);
        this.createElements(add, context);
        this.updateElements(update, context);
        const { animation, silence } = context;
        return this.context.animation.animate(animation, silence
            ? {}
            : {
                before: () => this.emit(new GraphLifeCycleEvent(GraphEvent.BEFORE_DRAW, { dataChanges, animation }), context),
                beforeAnimate: (animation) => this.emit(new AnimateEvent(GraphEvent.BEFORE_ANIMATE, AnimationType.DRAW, animation, drawData), context),
                afterAnimate: (animation) => this.emit(new AnimateEvent(GraphEvent.AFTER_ANIMATE, AnimationType.DRAW, animation, drawData), context),
                after: () => this.emit(new GraphLifeCycleEvent(GraphEvent.AFTER_DRAW, { dataChanges, animation }), context),
            });
    }
    computeChangesAndDrawData(context) {
        const { model } = this.context;
        const dataChanges = model.getChanges();
        const tasks = reduceDataChanges(dataChanges);
        if (tasks.length === 0)
            return null;
        const { NodeAdded = [], NodeUpdated = [], NodeRemoved = [], EdgeAdded = [], EdgeUpdated = [], EdgeRemoved = [], ComboAdded = [], ComboUpdated = [], ComboRemoved = [], } = groupBy(tasks, (change) => change.type);
        const dataOf = (data) => new Map(data.map((datum) => {
            const data = datum.value;
            return [idOf(data), data];
        }));
        const input = {
            add: {
                nodes: dataOf(NodeAdded),
                edges: dataOf(EdgeAdded),
                combos: dataOf(ComboAdded),
            },
            update: {
                nodes: dataOf(NodeUpdated),
                edges: dataOf(EdgeUpdated),
                combos: dataOf(ComboUpdated),
            },
            remove: {
                nodes: dataOf(NodeRemoved),
                edges: dataOf(EdgeRemoved),
                combos: dataOf(ComboRemoved),
            },
        };
        const drawData = this.transformData(input, context);
        // 清空变更 / Clear changes
        model.clearChanges();
        return { dataChanges, drawData };
    }
    transformData(input, context) {
        const transforms = this.context.transform.getTransformInstance();
        return Object.values(transforms).reduce((data, transform) => transform.beforeDraw(data, context), input);
    }
    createElement(elementType, datum, context) {
        const id = idOf(datum);
        const currentElement = this.getElement(id);
        if (currentElement)
            return;
        const type = this.getElementType(elementType, datum);
        const style = this.getElementComputedStyle(elementType, datum);
        // get shape constructor
        const Ctor = getExtension(elementType, type);
        if (!Ctor)
            return print.warn(`The element ${type} of ${elementType} is not registered.`);
        this.emit(new ElementLifeCycleEvent(GraphEvent.BEFORE_ELEMENT_CREATE, elementType, datum), context);
        const element = this.container.appendChild(new Ctor({
            id,
            style: {
                context: this.context,
                ...style,
            },
        }));
        this.shapeTypeMap[id] = type;
        this.elementMap[id] = element;
        const { stage = 'enter' } = context;
        this.context.animation?.add({
            element,
            elementType,
            stage,
            originalStyle: { ...element.attributes },
            modifiedStyle: { ...element.attributes, ...style },
        }, {
            after: () => {
                this.emit(new ElementLifeCycleEvent(GraphEvent.AFTER_ELEMENT_CREATE, elementType, datum), context);
                element.onCreate?.();
            },
        });
    }
    createElements(data, context) {
        const { nodes, edges, combos } = data;
        const iteration = [
            ['node', nodes],
            ['combo', combos],
            ['edge', edges],
        ];
        iteration.forEach(([elementType, elementData]) => {
            elementData.forEach((datum) => this.createElement(elementType, datum, context));
        });
    }
    updateElement(elementType, datum, context) {
        const id = idOf(datum);
        const element = this.getElement(id);
        if (!element)
            return () => null;
        this.emit(new ElementLifeCycleEvent(GraphEvent.BEFORE_ELEMENT_UPDATE, elementType, datum), context);
        const type = this.getElementType(elementType, datum);
        const style = this.getElementComputedStyle(elementType, datum);
        // 如果类型不同，需要先销毁原有元素，再创建新元素
        // If the type is different, you need to destroy the original element first, and then create a new element
        if (this.shapeTypeMap[id] !== type) {
            element.destroy();
            delete this.shapeTypeMap[id];
            delete this.elementMap[id];
            this.createElement(elementType, datum, { animation: false, silence: true });
        }
        const { stage = 'update' } = context;
        const exactStage = stage !== 'visibility' ? stage : style.visibility === 'hidden' ? 'hide' : 'show';
        this.context.animation?.add({
            element,
            elementType,
            stage: exactStage,
            originalStyle: { ...element.attributes },
            modifiedStyle: { ...element.attributes, ...style },
        }, {
            before: () => {
                // 通过 elementMap[id] 访问最新的 element，防止 type 不同导致的 element 丢失
                // Access the latest element through elementMap[id] to prevent the loss of element caused by different types
                const element = this.elementMap[id];
                if (stage !== 'collapse')
                    updateStyle(element, style);
                if (stage === 'visibility') {
                    // 缓存原始透明度 / Cache original opacity
                    // 会在 animation controller 中访问该缓存值 / The cached value will be accessed in the animation controller
                    if (!hasCachedStyle(element, 'opacity'))
                        cacheStyle(element, 'opacity');
                    setCacheStyle(element, 'visibility', exactStage === 'show' ? 'visible' : 'hidden');
                    if (exactStage === 'show')
                        updateStyle(element, { visibility: 'visible' });
                }
            },
            after: () => {
                const element = this.elementMap[id];
                if (stage === 'collapse')
                    updateStyle(element, style);
                if (exactStage === 'hide')
                    updateStyle(element, { visibility: getCachedStyle(element, 'visibility') });
                this.emit(new ElementLifeCycleEvent(GraphEvent.AFTER_ELEMENT_UPDATE, elementType, datum), context);
                element.onUpdate?.();
            },
        });
    }
    updateElements(data, context) {
        const { nodes, edges, combos } = data;
        const iteration = [
            ['node', nodes],
            ['combo', combos],
            ['edge', edges],
        ];
        iteration.forEach(([elementType, elementData]) => {
            elementData.forEach((datum) => this.updateElement(elementType, datum, context));
        });
    }
    /**
     * <zh/> 标记销毁元素
     *
     * <en/> mark destroy element
     * @param data - <zh/> 绘制数据 | <en/> draw data
     */
    markDestroyElement(data) {
        Object.values(data.remove).forEach((elementData) => {
            elementData.forEach((datum) => {
                const id = idOf(datum);
                const element = this.getElement(id);
                if (element)
                    markToBeDestroyed(element);
            });
        });
    }
    destroyElement(elementType, datum, context) {
        const { stage = 'exit' } = context;
        const id = idOf(datum);
        const element = this.elementMap[id];
        if (!element)
            return () => null;
        this.emit(new ElementLifeCycleEvent(GraphEvent.BEFORE_ELEMENT_DESTROY, elementType, datum), context);
        this.context.animation?.add({
            element,
            elementType,
            stage,
            originalStyle: { ...element.attributes },
            modifiedStyle: { ...element.attributes },
        }, {
            after: () => {
                this.clearElement(id);
                element.destroy();
                element.onDestroy?.();
                this.emit(new ElementLifeCycleEvent(GraphEvent.AFTER_ELEMENT_DESTROY, elementType, datum), context);
            },
        });
    }
    destroyElements(data, context) {
        const { nodes, edges, combos } = data;
        const iteration = [
            ['combo', combos],
            ['edge', edges],
            ['node', nodes],
        ];
        iteration.forEach(([elementType, elementData]) => {
            elementData.forEach((datum) => this.destroyElement(elementType, datum, context));
        });
        // TODO 重新计算色板样式，如果是分组色板，则不需要重新计算
    }
    clearElement(id) {
        delete this.paletteStyle[id];
        delete this.defaultStyle[id];
        delete this.stateStyle[id];
        delete this.elementMap[id];
        delete this.shapeTypeMap[id];
    }
    /**
     * <zh/> 收起节点
     *
     * <en/> collapse node
     * @param id - <zh/> 元素 ID | <en/> element ID
     * @param animation - <zh/> 是否使用动画，默认为 true | <en/> Whether to use animation, default is true
     */
    async collapseNode(id, animation) {
        const { model, layout } = this.context;
        const preprocess = this.computeChangesAndDrawData({ stage: 'collapse', animation });
        this.markDestroyElement(preprocess.drawData);
        // 进行预布局，计算出所有元素的位置
        // Perform pre-layout to calculate the position of all elements
        const result = await layout.simulate();
        model.updateData(result);
        // 重新计算数据 / Recalculate data
        const { drawData } = this.computeChangesAndDrawData({ stage: 'collapse', animation });
        const { add, remove, update } = drawData;
        this.markDestroyElement(drawData);
        const context = { animation, stage: 'collapse', data: drawData };
        this.destroyElements(remove, context);
        this.createElements(add, context);
        this.updateElements(update, context);
        await this.context.animation.animate(animation, {
            beforeAnimate: (animation) => this.emit(new AnimateEvent(GraphEvent.BEFORE_ANIMATE, AnimationType.COLLAPSE, animation, drawData), context),
            afterAnimate: (animation) => this.emit(new AnimateEvent(GraphEvent.AFTER_ANIMATE, AnimationType.COLLAPSE, animation, drawData), context),
        }, {
            collapse: {
                target: id,
                descendants: Array.from(remove.nodes).map(([, node]) => idOf(node)),
                position: positionOf(update.nodes.get(id)),
            },
        })?.finished;
    }
    /**
     * <zh/> 展开节点
     *
     * <en/> expand node
     * @param id - <zh/> 元素 ID | <en/> element ID
     * @param animation - <zh/> 是否使用动画，默认为 true | <en/> Whether to use animation, default is true
     */
    async expandNode(id, animation) {
        const { model, layout } = this.context;
        if (!model.getAncestorsData(id, COMBO_KEY).every((datum) => isCollapsed(datum)))
            return;
        const position = positionOf(model.getNodeData([id])[0]);
        // 首先创建展开的元素，然后进行预布局
        // First create the expanded element, then perform pre-layout
        const { drawData: { add }, } = this.computeChangesAndDrawData({ stage: 'collapse', animation });
        this.createElements(add, { animation: false, stage: 'expand' });
        // 重置动画 / Reset animation
        this.context.animation.clear();
        const result = await layout.simulate();
        model.updateData(result);
        // 重新计算数据 / Recalculate data
        this.computeStyle('expand');
        const { drawData } = this.computeChangesAndDrawData({ stage: 'collapse', animation });
        const { update } = drawData;
        const context = { animation, stage: 'expand', data: drawData };
        // 将新增边添加到更新列表 / Add new edges to the update list
        add.edges.forEach((edge) => {
            const id = idOf(edge);
            if (!update.edges.has(id))
                update.edges.set(id, edge);
        });
        this.updateElements(update, context);
        await this.context.animation.animate(animation, {
            beforeAnimate: (animation) => this.emit(new AnimateEvent(GraphEvent.BEFORE_ANIMATE, AnimationType.EXPAND, animation, drawData), context),
            afterAnimate: (animation) => this.emit(new AnimateEvent(GraphEvent.AFTER_ANIMATE, AnimationType.EXPAND, animation, drawData), context),
        }, {
            expand: {
                target: id,
                descendants: Array.from(add.nodes).map(([, node]) => idOf(node)),
                position,
            },
        })?.finished;
    }
    async collapseCombo(id, animation) {
        const { model, element } = this.context;
        if (model.getAncestorsData(id, COMBO_KEY).some((datum) => isCollapsed(datum)))
            return;
        const combo = element.getElement(id);
        const position = combo.getComboPosition({
            ...combo.attributes,
            collapsed: true,
        });
        const { dataChanges, drawData } = this.computeChangesAndDrawData({ stage: 'collapse', animation });
        this.markDestroyElement(drawData);
        const { update, remove } = drawData;
        const context = { animation, stage: 'collapse', data: drawData };
        this.destroyElements(remove, context);
        this.updateElements(update, context);
        const idsOf = (data) => Array.from(data).map(([, node]) => idOf(node));
        await this.context.animation.animate(animation, {
            before: () => this.emit(new GraphLifeCycleEvent(GraphEvent.BEFORE_DRAW, { dataChanges, animation }), context),
            beforeAnimate: (animation) => this.emit(new AnimateEvent(GraphEvent.BEFORE_ANIMATE, AnimationType.COLLAPSE, animation, drawData), context),
            afterAnimate: (animation) => this.emit(new AnimateEvent(GraphEvent.AFTER_ANIMATE, AnimationType.COLLAPSE, animation, drawData), context),
            after: () => this.emit(new GraphLifeCycleEvent(GraphEvent.AFTER_DRAW, { dataChanges, animation }), context),
        }, {
            collapse: {
                target: id,
                descendants: [...idsOf(remove.nodes), ...idsOf(remove.combos)],
                position,
            },
        })?.finished;
    }
    async expandCombo(id, animation) {
        const { model } = this.context;
        const position = positionOf(model.getComboData([id])[0]);
        // 重新计算数据 / Recalculate data
        this.computeStyle('expand');
        const { dataChanges, drawData } = this.computeChangesAndDrawData({ stage: 'expand', animation });
        const { add, update } = drawData;
        const context = { animation, stage: 'expand', data: drawData };
        this.createElements(add, context);
        this.updateElements(update, context);
        const idsOf = (data) => Array.from(data).map(([, node]) => idOf(node));
        await this.context.animation.animate(animation, {
            before: () => this.emit(new GraphLifeCycleEvent(GraphEvent.BEFORE_DRAW, { dataChanges, animation }), context),
            beforeAnimate: (animation) => this.emit(new AnimateEvent(GraphEvent.BEFORE_ANIMATE, AnimationType.EXPAND, animation, drawData), context),
            afterAnimate: (animation) => this.emit(new AnimateEvent(GraphEvent.AFTER_ANIMATE, AnimationType.EXPAND, animation, drawData), context),
            after: () => this.emit(new GraphLifeCycleEvent(GraphEvent.AFTER_DRAW, { dataChanges, animation }), context),
        }, {
            expand: {
                target: id,
                descendants: [...idsOf(add.nodes), ...idsOf(add.combos)],
                position,
            },
        })?.finished;
    }
    /**
     * <zh/> 计算元素置顶后的 zIndex
     *
     * <en/> Calculate the zIndex after the element is placed on top
     * @param id - <zh/> 元素 ID | <en/> ID of the element
     * @returns <zh/> zIndex | <en/> zIndex
     */
    getFrontZIndex(id) {
        const { model } = this.context;
        const elementType = model.getElementType(id);
        const elementData = model.getElementDataById(id);
        const data = model.getData();
        // 排除当前元素 / Exclude the current element
        Object.assign(data, {
            [`${elementType}s`]: data[`${elementType}s`].filter((element) => idOf(element) !== id),
        });
        if (elementType === 'combo') {
            // 如果 combo 展开，则排除 combo 的子节点/combo 及内部边
            // If the combo is expanded, exclude the child nodes/combos of the combo and the internal edges
            if (!isCollapsed(elementData)) {
                const ancestors = model.getAncestorsData(id, COMBO_KEY).map(idOf);
                data.nodes = data.nodes.filter((element) => !ancestors.includes(idOf(element)));
                data.combos = data.combos.filter((element) => !ancestors.includes(idOf(element)));
                data.edges = data.edges.filter(({ source, target }) => ancestors.includes(source) && ancestors.includes(target));
            }
        }
        return (Math.max(0, ...Object.values(data)
            .flat()
            .map((datum) => {
            const id = idOf(datum);
            return this.getElementZIndex(id);
        })) + 1);
    }
    destroy() {
        this.container.destroy();
        this.elementMap = {};
        this.shapeTypeMap = {};
        this.defaultStyle = {};
        this.stateStyle = {};
        this.paletteStyle = {};
        // @ts-expect-error force delete
        this.context = {};
    }
}
//# sourceMappingURL=element.js.map